import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { FORM_FIELD_TYPES } from "@domain/form";
import {
	Button,
	Checkbox,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Select,
	Space,
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { FormField, FormFieldOption, FormFieldType } from "../types";
import { isAnswerable, needsOptions } from "../types";
import { ConditionEditor } from "./ConditionEditor";

interface Props {
	field: FormField;
	/** Butun forma — shart muharriri (Task 6) uchun kerak bo'ladi. */
	allFields: FormField[];
	onChange: (next: FormField) => void;
}

/**
 * Turga bog'liq sozlamalarning kalitlari. Tur almashganda ular
 * TASHLANADI: `select` dan `number` ga o'tgan maydonda `options` qolsa,
 * `validateFormSchema` uni tekshirmaydi (u `options` ni faqat
 * select/multiselect uchun ko'radi) va o'lik sozlama nashr etilgan
 * sxemaga jimgina tushib qolardi.
 */
const TYPE_SPECIFIC_KEYS = [
	"options",
	// ⚠️ `pattern` shu ro'yxatda BO'LISHI SHART (kontrakt §3, spec §4.1 —
	// u faqat `text` ning sozlamasi). Bo'lmasa `text` → `number` o'tishi
	// o'lik `pattern` ni sxemada qoldirardi — aynan bu massiv oldini
	// oladigan xato.
	"pattern",
	"minLength",
	"maxLength",
	"min",
	"max",
	"step",
	"minDate",
	"maxDate",
	"minSelected",
	"maxSelected",
] as const;

function stripTypeSettings(field: FormField): FormField {
	const next: Record<string, unknown> = { ...field };

	for (const key of TYPE_SPECIFIC_KEYS) {
		delete next[key];
	}

	return next as FormField;
}

function OptionsEditor({
	options,
	onChange,
}: {
	options: FormFieldOption[];
	onChange: (next: FormFieldOption[]) => void;
}) {
	const { t } = useTranslation("leadForms");

	return (
		<Form.Item label={t("fieldSettings.options.label")}>
			<Space direction="vertical" className="w-full">
				{options.map((option, index) => (
					<Space key={`${option.value}-${index}`} data-testid="option-row">
						<Input
							value={option.value}
							placeholder={t("fieldSettings.options.valuePlaceholder")}
							onChange={(event) =>
								onChange(
									options.map((item, position) =>
										position === index ? { ...item, value: event.target.value } : item
									)
								)
							}
						/>
						<Input
							value={option.label}
							placeholder={t("fieldSettings.options.labelPlaceholder")}
							onChange={(event) =>
								onChange(
									options.map((item, position) =>
										position === index ? { ...item, label: event.target.value } : item
									)
								)
							}
						/>
						<Button
							type="text"
							danger
							icon={<DeleteOutlined />}
							title={t("fieldSettings.options.remove")}
							onClick={() => onChange(options.filter((_, position) => position !== index))}
						/>
					</Space>
				))}
				<Button
					icon={<PlusOutlined />}
					onClick={() =>
						onChange([
							...options,
							{ value: `variant_${options.length + 1}`, label: `Variant ${options.length + 1}` },
						])
					}
				>
					{t("fieldSettings.options.add")}
				</Button>
			</Space>
		</Form.Item>
	);
}

export function FieldSettingsPanel({ field, allFields, onChange }: Props) {
	const { t } = useTranslation("leadForms");
	const patch = (changes: Partial<FormField>) => onChange({ ...field, ...changes });

	/**
	 * ⚠️ Spec §4.3 invariant 1: shart faqat O'ZIDAN OLDIN kelgan maydonga
	 * havola qiladi. Kesish AYNAN shu yerda bajariladi — `ConditionEditor`
	 * kesmaydi, aks holda invariant ikki joyda yashab, biri eskirardi.
	 *
	 * `section` chiqarib tashlanadi: u javob bermaydi (spec §4.1), ya'ni
	 * unga qo'yilgan shart hech qachon rost bo'lmasdi.
	 */
	const index = allFields.findIndex((item) => item.key === field.key);
	const precedingFields = allFields.slice(0, index < 0 ? 0 : index).filter(isAnswerable);

	return (
		<Form layout="vertical">
			<Form.Item label={t("fieldSettings.keyLabel")} help={t("fieldSettings.keyHelp")}>
				<Input
					id="field-key"
					value={field.key}
					onChange={(event) => patch({ key: event.target.value })}
				/>
			</Form.Item>

			<Form.Item label={t("fieldSettings.labelLabel")}>
				<Input
					id="field-label"
					value={field.label}
					onChange={(event) => patch({ label: event.target.value })}
				/>
			</Form.Item>

			<Form.Item label={t("fieldSettings.typeLabel")}>
				<Select<FormFieldType>
					id="field-type"
					value={field.type}
					options={FORM_FIELD_TYPES.map((type) => ({
						value: type,
						label: <span data-testid={`type-${type}`}>{t(`fieldType.${type}`)}</span>,
					}))}
					onChange={(type) => {
						const cleaned = stripTypeSettings({ ...field, type });

						onChange(
							needsOptions(type)
								? { ...cleaned, options: [{ value: "variant_1", label: "Variant 1" }] }
								: cleaned
						);
					}}
				/>
			</Form.Item>

			{isAnswerable(field) && (
				<Form.Item>
					<Checkbox
						id="field-required"
						checked={field.required}
						onChange={(event) => patch({ required: event.target.checked })}
					>
						{t("fieldSettings.required")}
					</Checkbox>
				</Form.Item>
			)}

			<Form.Item label={t("fieldSettings.helpTextLabel")}>
				<Input
					id="field-help"
					value={field.helpText ?? ""}
					onChange={(event) => patch({ helpText: event.target.value || undefined })}
				/>
			</Form.Item>

			<Divider />

			{needsOptions(field.type) && (
				<OptionsEditor options={field.options ?? []} onChange={(options) => patch({ options })} />
			)}

			{(field.type === "text" || field.type === "textarea") && (
				<Space>
					<Form.Item label={t("fieldSettings.minLength")}>
						<InputNumber
							id="field-min-length"
							min={0}
							value={field.minLength ?? null}
							onChange={(value) => patch({ minLength: value ?? undefined })}
						/>
					</Form.Item>
					<Form.Item label={t("fieldSettings.maxLength")}>
						<InputNumber
							id="field-max-length"
							min={1}
							value={field.maxLength ?? null}
							onChange={(value) => patch({ maxLength: value ?? undefined })}
						/>
					</Form.Item>
				</Space>
			)}

			{/*
			 * ⚠️ `pattern` — FAQAT `text` uchun (spec §4.1: `textarea` da
			 * bor-yo'g'i `maxLength`). Bo'sh satr `undefined` ga
			 * aylantiriladi, aks holda `pattern: ""` sxemaga tushib,
			 * mobil renderer har javobni rad etardi.
			 */}
			{field.type === "text" && (
				<Form.Item label={t("fieldSettings.patternLabel")} help={t("fieldSettings.patternHelp")}>
					<Input
						id="field-pattern"
						value={field.pattern ?? ""}
						placeholder="^[A-Za-z' ]+$"
						onChange={(event) => patch({ pattern: event.target.value || undefined })}
					/>
				</Form.Item>
			)}

			{field.type === "number" && (
				<Space>
					<Form.Item label={t("fieldSettings.min")}>
						<InputNumber
							id="field-min"
							value={field.min ?? null}
							onChange={(value) => patch({ min: value ?? undefined })}
						/>
					</Form.Item>
					<Form.Item label={t("fieldSettings.max")}>
						<InputNumber
							id="field-max"
							value={field.max ?? null}
							onChange={(value) => patch({ max: value ?? undefined })}
						/>
					</Form.Item>
					<Form.Item label={t("fieldSettings.step")}>
						<InputNumber
							id="field-step"
							min={0}
							value={field.step ?? null}
							onChange={(value) => patch({ step: value ?? undefined })}
						/>
					</Form.Item>
				</Space>
			)}

			{/*
			 * ⚠️ **Kontrakt §3.2: `rating` da `min` E'TIBORSIZ QOLDIRILADI**
			 * (`packages/domain/src/form.ts:573` — quyi chegara 1 ga qattiq
			 * yozilgan, `field.min` umuman o'qilmaydi). Shu sababli faqat
			 * `max` (standart 5) taklif qilinadi: `min` ni ko'rsatish
			 * «konstruktorda ruxsat berilgan, serverda e'tiborsiz» tafovutini
			 * yaratardi — aynan shu xato sinfini `form.ts` oldini olishi
			 * kerak edi.
			 */}
			{field.type === "rating" && (
				<Form.Item label={t("fieldSettings.ratingMax")} help={t("fieldSettings.ratingMaxHelp")}>
					<InputNumber
						id="field-max"
						min={1}
						value={field.max ?? null}
						onChange={(value) => patch({ max: value ?? undefined })}
					/>
				</Form.Item>
			)}

			{field.type === "multiselect" && (
				<Space>
					<Form.Item label={t("fieldSettings.minSelected")}>
						<InputNumber
							id="field-min-selected"
							min={0}
							value={field.minSelected ?? null}
							onChange={(value) => patch({ minSelected: value ?? undefined })}
						/>
					</Form.Item>
					<Form.Item label={t("fieldSettings.maxSelected")}>
						<InputNumber
							id="field-max-selected"
							min={1}
							value={field.maxSelected ?? null}
							onChange={(value) => patch({ maxSelected: value ?? undefined })}
						/>
					</Form.Item>
				</Space>
			)}

			{field.type === "date" && (
				<Space>
					<Form.Item label={t("fieldSettings.minDate")}>
						<DatePicker
							id="field-min-date"
							value={field.minDate ? dayjs(field.minDate) : null}
							onChange={(value) => patch({ minDate: value?.format("YYYY-MM-DD") })}
						/>
					</Form.Item>
					<Form.Item label={t("fieldSettings.maxDate")}>
						<DatePicker
							id="field-max-date"
							value={field.maxDate ? dayjs(field.maxDate) : null}
							onChange={(value) => patch({ maxDate: value?.format("YYYY-MM-DD") })}
						/>
					</Form.Item>
				</Space>
			)}

			<Divider />

			<ConditionEditor
				conditions={field.visibleIf ?? []}
				precedingFields={precedingFields}
				onChange={(visibleIf) => patch({ visibleIf: visibleIf.length > 0 ? visibleIf : undefined })}
			/>
		</Form>
	);
}
